import { PurchaseHistoryListSectionStyled } from './PurchaseHistoryListSection.styled'
import { Button, OverlayTrigger } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { StyledTooltip } from './ArbitrationForm.styled'
import { useContext } from 'react'
import { PurchaseHistoryContext } from 'contexts/PurchaseHistoryContext'
import NoActivity from 'modules/BuyerActivity/NoActivity'
import { DataTable } from 'components/DataTableHistory'
import { Link } from 'components/Share/Link'
import CaretDownFill from 'images/icon/CaretDownFill'
import CaretRightFill from 'images/icon/CaretRightFill'
import PurchaseHistoryInfoSection from './PurchaseHistoryInfoSection'
import { PopoverAttachment } from 'modules/PopoverAttachment'
import { PaperClipButton } from 'modules/DealerVehicleManagement/VehicleDetails.styled'
import { faPaperclip } from '@fortawesome/free-solid-svg-icons'
import { IPurchaseHistoryResult } from 'types/purchaseHistoryTypes'
import { ChargePopover } from './ChargesPopOver'
import { Paging } from 'components/Paging'
import { useNewFormatDate } from 'hooks/useIntl'
import { ArbitrationState, IArbitrationRequest } from '../../types/arbitrationTypes'
import { useGlobalStore } from 'store/useGlobalStore'

const PERIOD_EXPIRED_TOOLTIP_MESSAGE =
  'The window for physical damage claims has closed. Non physical damage claims may be considered, please use the Contact Us form for review. Include VIN in your submission.'

interface IProps {
  onArbitrationClick: (request: IArbitrationRequest) => void
  onAddAttachmentsClick: (request: IArbitrationRequest) => void
}

export const PurchaseHistoryListSection = ({ onArbitrationClick, onAddAttachmentsClick }: IProps) => {
  const { result, searchFilter, updateSearchFilter } = useContext(PurchaseHistoryContext)
  const getLocalText = useGlobalStore((state) => state.getLocalText)

  const handlePageSizeChanged = (pageSizeSelected: number) => {
    updateSearchFilter({ ...searchFilter, PageSize: pageSizeSelected, PageNumber: 0 })
  }

  const formatDate = useNewFormatDate()

  const paginationClicked = (pageSelected: number) => {
    if (pageSelected === searchFilter.PageNumber + 1) return

    updateSearchFilter({ ...searchFilter, PageNumber: pageSelected - 1 })
  }

  const showArbitrationColumn = result?.Items?.[0]?.ShowArbitrationColumn ?? false

  const arbitrationColumn = {
    Header: 'Arbitration',
    accessor: (x: IPurchaseHistoryResult) => x,
    Cell: ({ value }) => {
      const {
        CanAddDocuments,
        VehicleInstanceID,
        BuyerVehiclePurchaseID,
        ArbitrationState: arbitrationState,
        LatestArbitrationStatus: latestArbitrationStatus
      } = value
      if (arbitrationState === ArbitrationState.NEW) {
        return (
          <span
            className="arbitration-link"
            onClick={() =>
              onArbitrationClick({
                vehicleInstanceID: VehicleInstanceID,
                buyerVehiclePurchaseID: BuyerVehiclePurchaseID
              })
            }
          >
            ARBITRATE VEHICLE
          </span>
        )
      }
      if (arbitrationState === ArbitrationState.SUBMITTED) {
        const hasStatus = !!latestArbitrationStatus?.trim()
        const statusLabel = hasStatus ? latestArbitrationStatus.trim().toUpperCase() : 'ARBITRATION SUBMITTED'
        return (
          <div>
            <span className={hasStatus ? 'arbitration-status' : 'arbitration-submitted'}>{statusLabel}</span>
            {CanAddDocuments && (
              <span
                className="arbitration-link"
                onClick={() =>
                  onAddAttachmentsClick({
                    vehicleInstanceID: VehicleInstanceID,
                    buyerVehiclePurchaseID: BuyerVehiclePurchaseID
                  })
                }
              >
                ADD ATTACHMENTS
              </span>
            )}
          </div>
        )
      }
      if (arbitrationState === ArbitrationState.EXPIRED) {
        return (
          <span
            className="arbitration-expired"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}
          >
            ARBITRATION PERIOD EXPIRED
            <OverlayTrigger
              placement="top"
              overlay={
                <StyledTooltip id="arbitration-expired-tooltip">
                  {getLocalText('ArbitrationPeriodExpired', PERIOD_EXPIRED_TOOLTIP_MESSAGE)}
                </StyledTooltip>
              }
            >
              <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            </OverlayTrigger>
          </span>
        )
      }
      return null
    }
  }

  return (
    <PurchaseHistoryListSectionStyled>
      {result && result?.Items?.length > 0 ? (
        <>
          <DataTable
            data={result?.Items}
            isFilterSearch={false}
            // filterColumnAcesscor="Title"
            isDataPaginated={true}
            columns={[
              {
                id: 'Title',
                Header: 'Description',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => (
                  <Link className="link-history" to={`vehicle/${value.VehicleInstanceID}?history=true`}>
                    {value.Title}
                  </Link>
                )
              },
              {
                id: 'Vin',
                Header: 'VIN',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => (
                  <>
                    {value.Vin}
                    {value?.ExternalNoteAttachmentDocuments?.length > 0 && (
                      <OverlayTrigger
                        placement="bottom"
                        trigger="click"
                        rootClose
                        overlay={(props) => (
                          <PopoverAttachment VIN={value.Vin} vehicleInstanceID={value.VehicleInstanceID} {...props} />
                        )}
                      >
                        <PaperClipButton variant="link">
                          <FontAwesomeIcon icon={faPaperclip} title="Click Here to View Attachments" />
                        </PaperClipButton>
                      </OverlayTrigger>
                    )}
                  </>
                )
              },
              {
                Header: 'Sale Price',
                accessor: 'Charges',
                Cell: ({ value }) => (
                  <> {value.find((x) => x.VehicleChargeTypeDescription === 'Sale Price')?.PriceString} </>
                )
              },
              {
                id: 'Buy Fee',
                Header: 'Buy Fee',
                accessor: 'Charges',
                Cell: ({ value }) => (
                  <> {value.find((x) => x.VehicleChargeTypeDescription === 'Buy Fee')?.PriceString} </>
                )
              },
              {
                id: 'Total Cost',
                Header: 'Total Cost',
                accessor: (x: IPurchaseHistoryResult) => x,
                Cell: ({ value }) => (
                  <div>
                    {value?.Charges?.filter(
                      (x) =>
                        x.VehicleChargeTypeDescription !== 'Buy Fee' && x.VehicleChargeTypeDescription !== 'Sale Price'
                    ).length > 0 ? (
                      <OverlayTrigger
                        placement="top-end"
                        trigger="click"
                        rootClose
                        overlay={(props) => <ChargePopover charges={value?.Charges} {...props} />}
                      >
                        <Button variant="link" className="charges-link">
                          {value.TotalPriceString}
                        </Button>
                      </OverlayTrigger>
                    ) : (
                      <Button variant="link" className="price-str" disabled>
                        {value.TotalPriceString}
                      </Button>
                    )}
                  </div>
                )
              },
              {
                Header: 'Purchase Date',
                accessor: 'PurchaseDate',
                Cell: ({ value }) => <>{formatDate(value)}</>
              },
              {
                Header: 'Payment Method',
                accessor: 'PaymentType'
              },
              {
                Header: 'Document',
                accessor: 'Documents',
                Cell: ({ value }) => {
                  return (
                    <div className="documents">
                      {value?.length > 0
                        ? value.map((doc) => (
                            <Link to={doc.DocumentURL} className="link-history">
                              {doc.display}
                            </Link>
                          ))
                        : 'NA'}
                    </div>
                  )
                }
              },
              ...(showArbitrationColumn ? [arbitrationColumn] : []),
              {
                Header: () => null,
                id: 'expander',
                Cell: ({ row }) => (
                  <span className="action" {...row.getToggleRowExpandedProps()}>
                    {row.isExpanded ? <CaretDownFill /> : <CaretRightFill />}
                  </span>
                )
              }
            ]}
            renderRowSubComponent={(row) => (
              <PurchaseHistoryInfoSection VIN={row?.original?.Vin} isLoadApi={row.isExpanded} />
            )}
          />
          <Paging
            currentPage={searchFilter.PageNumber}
            currentPageSize={searchFilter.PageSize}
            handleChangedPage={paginationClicked}
            handlePageSizeChanged={handlePageSizeChanged}
            totalPages={result.TotalPages}
          />
        </>
      ) : (
        <NoActivity message={'No Purchase Vehicle Found'} />
      )}
    </PurchaseHistoryListSectionStyled>
  )
}
